import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useState } from 'react';
import type { Nullable, Optional } from '@gov-nx/core/types/helpers.types';
import type { AppLoaderControl } from '@gov-nx/core/types/loader.types';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import type { ServiceCode } from '@gov-nx/module/service/definitions/codes';
import type { AppContextType } from './AppContext.types';

const AppContext = createContext<Nullable<AppContextType>>(null);

export interface AppContextProviderProps extends PropsWithChildren {
	loaderControl?: AppLoaderControl;
}

export function AppContextProvider({
	children,
	loaderControl,
}: AppContextProviderProps) {
	const [isDashboardPage, setDashboardPage] = useState<boolean>(false);
	const [loading, setAppLoading] = useState<boolean>(true);
	const [loadingMessage, setLoadingMessage] =
		useState<Optional<string>>(undefined);
	const [appError, setAppError] = useState<Error | null>(null);

	const [currentPageCode, setCurrentPageCode] =
		useState<Nullable<PageCode>>(null);
	const [currentServiceCode, setCurrentServiceCode] =
		useState<Nullable<ServiceCode>>(null);

	const setCurrentCode = (
		pageCode: Nullable<PageCode>,
		serviceCode: Nullable<ServiceCode>
	) => {
		if (pageCode) {
			setCurrentPageCode(pageCode);
			setCurrentServiceCode(null);
		} else if (serviceCode) {
			setCurrentPageCode(null);
			setCurrentServiceCode(serviceCode);
		}
	};

	const setLoading = (status: boolean, message?: string) => {
		setAppLoading(status);
		setLoadingMessage(message);

		if (loaderControl?.show() && status) {
			loaderControl.show(message);
		}
		if (loaderControl?.hide() && !status) {
			loaderControl?.hide();
		}
	};

	return (
		<AppContext.Provider
			value={{
				loading,
				setLoading,
				loadingMessage,
				setLoadingMessage,
				appError,
				setAppError,
				isDashboardPage,
				setDashboardPage,
				currentPageCode,
				currentServiceCode,
				setCurrentCode,
			}}>
			{children}
		</AppContext.Provider>
	);
}

export const useAppContext = (): AppContextType =>
	useContext(AppContext) as AppContextType;
